import { createMockContact, createMockSlugData } from '@indutrust/shared-model';
import type { FactoryEdit } from '../factory-edit';

/**
 * Map / discovery id for Advanced Armour Engineering (SAIF Zone).
 * The 20-node virtual-tour graph and the plant-layout machine park
 * are seeded against this id.
 */
export const MOCK_AAE_FACTORY_ID = 43;

const AAE_CAPABILITIES = [
  createMockSlugData({ id: 11, name: 'Milling', slug: 'milling' }),
  createMockSlugData({ id: 12, name: 'Turning', slug: 'turning' }),
  createMockSlugData({ id: 31, name: 'Stamping', slug: 'stamping' }),
  createMockSlugData({ id: 41, name: 'MIG / MAG', slug: 'mig-mag' }),
  createMockSlugData({ id: 42, name: 'TIG', slug: 'tig' }),
  createMockSlugData({
    id: 111,
    name: 'Laser Cutting',
    slug: 'laser-cutting',
  }),
  createMockSlugData({
    id: 113,
    name: 'Water Jet Cutting',
    slug: 'water-jet-cutting',
  }),
  createMockSlugData({
    id: 121,
    name: 'Powder Coating',
    slug: 'powder-coating',
  }),
  createMockSlugData({
    id: 141,
    name: 'Mechanical Assembly',
    slug: 'mechanical-assembly',
  }),
  createMockSlugData({
    id: 211,
    name: 'Brackets & Mounts',
    slug: 'brackets-mounts',
  }),
] as const;

const AAE_CAPABILITY_IDS = AAE_CAPABILITIES.map((item) => item.id);

/**
 * Complete published profile for Advanced Armour Engineering.
 *
 * Plant location, floor area, and process list come from the AAE zone
 * plant-layout and public company pages. Headcount, turnover, contacts,
 * and trade-license values are invented demo data — not claims about
 * the real company.
 */
export const MOCK_FACTORY_AAE: FactoryEdit = {
  id: MOCK_AAE_FACTORY_ID,
  slug: 'advanced-armour-engineering',
  name: 'Advanced Armour Engineering',
  status: 'published',
  verified: true,
  created_at: '2014-06-01T00:00:00Z',
  updated_at: '2026-03-15T00:00:00Z',
  country_id: 1,
  industrial_area_id: 15,
  industrial_area_name: 'Saif Zone',
  industrial_subtypes: [
    createMockSlugData({
      id: 1,
      name: 'CNC Machining',
      slug: 'cnc-machining',
    }),
    createMockSlugData({
      id: 3,
      name: 'Metal Forming',
      slug: 'metal-forming',
    }),
    createMockSlugData({ id: 4, name: 'Welding', slug: 'welding' }),
    createMockSlugData({
      id: 5,
      name: 'Metal Fabrication',
      slug: 'metal-fabrication',
    }),
    createMockSlugData({
      id: 6,
      name: 'Surface Treatment',
      slug: 'surface-treatment',
    }),
    createMockSlugData({ id: 7, name: 'Assembly', slug: 'assembly' }),
  ],
  industrial_subtype_ids: [1, 3, 4, 5, 6, 7],
  capabilities: [...AAE_CAPABILITIES],
  capability_ids_array: [...AAE_CAPABILITY_IDS],
  capability_ids: [...AAE_CAPABILITY_IDS],
  address:
    'Building T5-25, Sharjah Airport International Freezone (SAIF Zone), Sharjah, United Arab Emirates',
  po_box: 'SAIF Zone, Sharjah, UAE',
  latitude: 25.3445339,
  longitude: 55.5000964,
  description:
    'British-owned armour-steel fabrication plant in SAIF Zone with ' +
    '7,500 m² of secure factory floor. Design, laser cutting, water-jet ' +
    'cutting, press-brake forming, welding, CNC machining, shotblast, ' +
    'paint, and assembly of ballistic and blast-protection components ' +
    'for defence, security, aerospace, and complex fabrications. ' +
    'On-site engineering and design department supports Catia, ' +
    'SolidWorks, and Inventor industrialisation.',
  creation_date: '2014-01-01',
  website: 'https://www.armoureng.com',
  linked_in_url: 'https://www.linkedin.com/company/advanced-armour-engineering',
  project_references: [],
  project_ref_on_demand: false,
  turnover: null,
  turnover_on_demand: true,
  company_size: 85,
  headcount_production: 62,
  headcount_management: 18,
  workshop_used: 7500,
  workshop_available: 0,
  trade_license_number: 120156789034,
  certification: ['ISO 9001:2015'],
  logo: 'assets/images/factories/aae-logo.png',
  images: [],
  current_capabilities: [...AAE_CAPABILITY_IDS],
  shareholder_uae: 'British-owned',
  product_reference:
    'Armour kits, BlastRider seats, fuel tanks, wheel protection kits, weapon mounts',
  activity_reference:
    'Laser cutting, water-jet cutting, press-brake forming, welding, CNC machining, shotblast, painting, assembly',
  contacts: [
    createMockContact({
      id: 43,
      type: 'sales',
      first_name: 'James',
      last_name: 'Whitaker',
      email: 'sales@aae.demo',
      number: '+971 6 552 9353',
      has_whatsapp: true,
      country_id: 1,
      country_code: 'AE',
      indutrustMainPoc: true,
    }),
    createMockContact({
      id: 44,
      type: 'admin',
      first_name: 'Layla',
      last_name: 'Al Mansoori',
      email: 'operations@aae.demo',
      number: '+971 6 552 9353',
      has_whatsapp: false,
      country_id: 1,
      country_code: 'AE',
      indutrustMainPoc: false,
    }),
  ],
  prospects: [],
  can_edit: true,
};
